import type React from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import {
  OrdReferenceInput,
  OrdBulkUpdateFormButton,
  OrdEditInDialogButton,
  OrdFormList,
  useFormType,
  OrdDateTimeInput,
  OrdIconStandard,
  OrdGridTabbedForm,
  OrdFormCard,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { QueueUpdateFields, QueueSettingsFields, dataSchema } from '@ragu-components/ord-types';
import {
  TabbedForm,
  TextInput,
  NumberInput,
  FormDataConsumer,
  required,
  useRecordContext,
  useRefresh,
  SelectInput,
  useTranslate,
  useCanAccess,
} from 'react-admin';

import { OrdDescriptionInput, OrdInlineFields } from 'src/components/formInputs';

import AssignAgentsForm from '../agents/AssignAgentsForm';

import QueueAgentsPreview from './QueueAgentsPreview';

import type { OrdGridTabbedFormProps } from '@ragu-components/ord-components';
import type { Queue, QueueUpdate } from '@ragu-components/ord-types';

const dateParser = (date: Date | null): string | null | undefined =>
  date !== undefined && date !== null ? date.toISOString() : '';

/**
 * Generate a form for managing queues with tabs for details and settings.
 *
 * @param {OrdGridTabbedFormProps} props - Props for the form
 * @return {React.ReactElement} The rendered QueuesForm component
 */
export const QueuesForm = (props: OrdGridTabbedFormProps): React.ReactElement => {
  const { create } = useFormType();
  const refresh = useRefresh();
  const record = useRecordContext<Queue>();
  const translate = useTranslate();
  const { canAccess: canListAgents } = useCanAccess({ resource: Resources.agents, action: 'list' });
  const { canAccess: canCreateAgents } = useCanAccess({ resource: Resources.agents, action: 'create' });
  const { canAccess: canEditAgents } = useCanAccess({ resource: Resources.agents, action: 'edit' });
  const canAssignAgents = canCreateAgents === true || canEditAgents === true;

  return (
    <OrdGridTabbedForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={dataSchema.QueueCreate}
      editJsonSchema={dataSchema.QueueUpdate}
      {...props}
    >
      <TabbedForm.Tab label='resources.queues.operations.sections.details'>
        <OrdFormCard
          half
          title={translate('resources.queues.operations.sections.subsections.general_info')}
          icon={<OrdIconStandard name='edit' />}
        >
          <TextInput source={QueueUpdateFields.name} isRequired resettable={create} autoComplete='off' fullWidth />
          <NumberInput
            source={QueueUpdateFields.number}
            isRequired
            helperText='resources.queues.helperTexts.number'
            fullWidth
          />
          <OrdDescriptionInput source={QueueUpdateFields.description} autoComplete='off' fullWidth />
        </OrdFormCard>

        <OrdFormCard
          half
          spacing={0}
          title={translate('resources.agents.name')}
          icon={<OrdIconStandard name='agents' />}
        >
          {create ? (
            <Typography color='text.secondary' fontStyle='italic'>
              {translate('resources.queues.messages.save_first_for_agents')}
            </Typography>
          ) : !canListAgents ? null : record === null || record === undefined ? (
            <CircularProgress size={24} />
          ) : (
            <QueueAgentsPreview
              queueId={record.id}
              actionButton={
                canAssignAgents ? (
                  <OrdEditInDialogButton
                    resource={Resources.queues}
                    mutationMode='pessimistic'
                    fullWidth
                    label='resources.agents.operations.assign_agents'
                    icon={<></>}
                    title='resources.agents.operations.assign_agents'
                    ButtonProps={{ variant: 'contained' }}
                  >
                    <AssignAgentsForm onAgentsUpdateEvent={refresh} />
                  </OrdEditInDialogButton>
                ) : undefined
              }
            />
          )}
        </OrdFormCard>

        <OrdFormCard
          title={translate('resources.queues.operations.sections.subsections.audio_strategy')}
          icon={<OrdIconStandard name='soundfiles' />}
        >
          <OrdInlineFields>
            <OrdReferenceInput
              resource={Resources.soundfiles}
              isRequired
              source={QueueUpdateFields.soundfile}
              fullWidth
            />
            <OrdReferenceInput resource={Resources.soundfiles} source={QueueUpdateFields.customsound} fullWidth />
          </OrdInlineFields>
          <SelectInput
            choices={dataSchema.QueueUpdate.properties.strategy.enum.map((strategy) => ({
              id: strategy,
              name: translate(`resources.queues.fields.strategies.${strategy}`),
            }))}
            validate={required()}
            source={QueueUpdateFields.strategy}
            variant='outlined'
            defaultValue={dataSchema.QueueUpdate.properties.strategy.default}
            helperText='resources.queues.helperTexts.strategy'
            fullWidth
          />
        </OrdFormCard>
      </TabbedForm.Tab>

      <TabbedForm.Tab label='resources.queues.operations.sections.callbacks'>
        <Box sx={{ width: '100%' }}>
          <JsonSchemaInput
            source={QueueUpdateFields['settings.callback_enabled']}
            helperText='resources.queues.helperTexts.settings.callback_enabled'
            fullWidth
          />
        </Box>
        <FormDataConsumer<QueueUpdate>>
          {({ formData, ...rest }) =>
            formData?.settings?.callback_enabled === true && (
              <>
                <OrdFormCard
                  half
                  title={translate('resources.queues.operations.sections.subsections.callback_announcements')}
                  icon={<OrdIconStandard name='callbacks' />}
                >
                  <OrdReferenceInput
                    resource={Resources.soundfiles}
                    source={QueueUpdateFields.callback_announce}
                    helperText='resources.queues.helperTexts.callback_announce'
                    fullWidth
                  />
                  <OrdReferenceInput
                    resource={Resources.soundfiles}
                    source={QueueUpdateFields.callback_book_announce}
                    helperText='resources.queues.helperTexts.callback_book_announce'
                    fullWidth
                  />
                  <OrdReferenceInput
                    resource={Resources.soundfiles}
                    source={QueueUpdateFields.callback_success_announce}
                    helperText='resources.queues.helperTexts.callback_success_announce'
                    fullWidth
                  />
                  <OrdReferenceInput
                    resource={Resources.calendars}
                    source={QueueUpdateFields.calendar}
                    fullWidth
                    {...rest}
                  />
                </OrdFormCard>

                <OrdFormCard
                  half
                  title={translate('resources.queues.operations.sections.subsections.callback_settings')}
                  icon={<OrdIconStandard name='settings' />}
                >
                  <JsonSchemaInput
                    source={QueueUpdateFields['settings.callback_caller_id_number']}
                    helperText='resources.queues.helperTexts.settings.callback_caller_id_number'
                    fullWidth
                  />
                  <JsonSchemaInput
                    source={QueueUpdateFields['settings.callback_caller_number_regex']}
                    helperText='resources.queues.helperTexts.settings.callback_caller_number_regex'
                    fullWidth
                  />
                  <OrdInlineFields>
                    <JsonSchemaInput
                      source={QueueUpdateFields['settings.callback_delay']}
                      helperText='resources.queues.helperTexts.settings.callback_delay'
                      fullWidth
                    />
                    <JsonSchemaInput
                      source={QueueUpdateFields['settings.callback_retention']}
                      helperText='resources.queues.helperTexts.settings.callback_retention'
                      fullWidth
                    />
                  </OrdInlineFields>
                  <OrdInlineFields>
                    <JsonSchemaInput
                      source={QueueUpdateFields['settings.callback_retries']}
                      helperText='resources.queues.helperTexts.settings.callback_retries'
                      fullWidth
                    />
                    <JsonSchemaInput
                      source={QueueUpdateFields['settings.callback_timeout']}
                      helperText='resources.queues.helperTexts.settings.callback_timeout'
                      fullWidth
                    />
                  </OrdInlineFields>
                  <JsonSchemaInput
                    source={QueueUpdateFields['settings.callback_username']}
                    helperText='resources.queues.helperTexts.settings.callback_username'
                    fullWidth
                  />
                  <JsonSchemaInput
                    source={QueueUpdateFields['settings.callback_wait_time']}
                    helperText='resources.queues.helperTexts.settings.callback_wait_time'
                    fullWidth
                  />
                </OrdFormCard>
              </>
            )
          }
        </FormDataConsumer>
      </TabbedForm.Tab>

      <TabbedForm.Tab label='resources.queues.operations.sections.settings'>
        <OrdFormCard
          half
          title={translate('resources.queues.operations.sections.subsections.capacity_alerts')}
          icon={<OrdIconStandard name='alarm' />}
        >
          <JsonSchemaInput
            source={QueueUpdateFields['settings.alarm_threshold']}
            helperText='resources.queues.helperTexts.settings.alarm_threshold'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.announce_position']}
            helperText='resources.queues.helperTexts.settings.announce_position'
            fullWidth
          />
        </OrdFormCard>

        <OrdFormCard
          half
          title={translate('resources.queues.operations.sections.subsections.caller_experience')}
          icon={<OrdIconStandard name='phone' />}
        >
          <JsonSchemaInput
            source={QueueUpdateFields['settings.announce_agent']}
            helperText='resources.queues.helperTexts.settings.announce_agent'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.announce_frequency']}
            helperText='resources.queues.helperTexts.settings.announce_frequency'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.announce_waiting']}
            helperText='resources.queues.helperTexts.settings.announce_waiting'
            fullWidth
          />
          <OrdInlineFields>
            <JsonSchemaInput
              source={QueueUpdateFields['settings.busy_delay_time']}
              helperText='resources.queues.helperTexts.settings.busy_delay_time'
              fullWidth
            />
            <JsonSchemaInput
              source={QueueUpdateFields['settings.call_timeout']}
              helperText='resources.queues.helperTexts.settings.call_timeout'
              fullWidth
            />
          </OrdInlineFields>
        </OrdFormCard>

        <OrdFormCard
          title={translate('resources.queues.operations.sections.subsections.advanced')}
          icon={<OrdIconStandard name='advanced' />}
          half
        >
          <OrdReferenceInput
            resource={Resources.call_recording_confs}
            source={QueueUpdateFields.call_recording_conf_id}
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.continue_on_answer']}
            helperText='resources.queues.helperTexts.settings.continue_on_answer'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.continue_on_max_waiters']}
            helperText='resources.queues.helperTexts.settings.continue_on_max_waiters'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.exitkey']}
            helperText='resources.queues.helperTexts.settings.exitkey'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.daily_reset_stats']}
            helperText='resources.queues.helperTexts.settings.daily_reset_stats'
            fullWidth
          />
          <OrdDateTimeInput
            allDay={false}
            parse={dateParser}
            source={QueueUpdateFields['settings.daily_reset_stats_time']}
            helperText='resources.queues.helperTexts.settings.daily_reset_stats_time'
          />
        </OrdFormCard>

        <OrdFormCard
          half
          title={translate('resources.queues.operations.sections.subsections.agent_management')}
          icon={<OrdIconStandard name='agents' />}
        >
          <JsonSchemaInput
            source={QueueUpdateFields['settings.ignore_unavail']}
            helperText='resources.queues.helperTexts.settings.ignore_unavail'
            fullWidth
          />
          <OrdInlineFields>
            <JsonSchemaInput
              source={QueueUpdateFields['settings.max_exten']}
              helperText='resources.queues.helperTexts.settings.max_exten'
              fullWidth
            />
            <JsonSchemaInput
              source={QueueUpdateFields['settings.max_wait_time']}
              helperText='resources.queues.helperTexts.settings.max_wait_time'
              fullWidth
            />
          </OrdInlineFields>
          <OrdInlineFields>
            <JsonSchemaInput
              source={QueueUpdateFields['settings.no_answer_delay_time']}
              helperText='resources.queues.helperTexts.settings.no_answer_delay_time'
              fullWidth
            />
            <JsonSchemaInput
              source={QueueUpdateFields['settings.reject_delay_time']}
              helperText='resources.queues.helperTexts.settings.reject_delay_time'
              fullWidth
            />
          </OrdInlineFields>
          <JsonSchemaInput
            source={QueueUpdateFields['settings.service_level']}
            helperText='resources.queues.helperTexts.settings.service_level'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.skip_empty']}
            helperText='resources.queues.helperTexts.settings.skip_empty'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.weight']}
            helperText='resources.queues.helperTexts.settings.weight'
            fullWidth
          />
          <JsonSchemaInput
            source={QueueUpdateFields['settings.wrap_up_time']}
            helperText='resources.queues.helperTexts.settings.wrap_up_time'
            fullWidth
          />
        </OrdFormCard>
      </TabbedForm.Tab>
    </OrdGridTabbedForm>
  );
};

/** Form for bulk editing queues */
export const QueuesBulkEditForm = (): React.ReactElement => {
  const jsonSchema = dataSchema.QueueSettings;

  const inputs = [
    {
      label: 'resources.queues.fields.soundfile',
      element: (
        <OrdFormList icon={<OrdIconStandard name='edit' />}>
          <OrdReferenceInput resource={Resources.soundfiles} source={QueueUpdateFields.soundfile} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.customsound',
      element: (
        <OrdFormList icon={<OrdIconStandard name='edit' />}>
          <OrdReferenceInput resource={Resources.soundfiles} source={QueueUpdateFields.customsound} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.calendar',
      element: (
        <OrdFormList icon={<OrdIconStandard name='edit' />}>
          <OrdReferenceInput resource={Resources.calendars} source={QueueUpdateFields.calendar} />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.alarm_threshold',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.alarm_threshold}
            source={`settings.${QueueSettingsFields.alarm_threshold}`}
            helperText='resources.queues.helperTexts.settings.alarm_threshold'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.announce_agent',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.announce_agent}
            source={`settings.${QueueSettingsFields.announce_agent}`}
            helperText='resources.queues.helperTexts.settings.announce_agent'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.announce_frequency',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.announce_frequency}
            source={`settings.${QueueSettingsFields.announce_frequency}`}
            helperText='resources.queues.helperTexts.settings.announce_frequency'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.announce_position',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.announce_position}
            source={`settings.${QueueSettingsFields.announce_position}`}
            helperText='resources.queues.helperTexts.settings.announce_position'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.announce_waiting',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.announce_waiting}
            source={`settings.${QueueSettingsFields.announce_waiting}`}
            helperText='resources.queues.helperTexts.settings.announce_waiting'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.busy_delay_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.busy_delay_time}
            source={`settings.${QueueSettingsFields.busy_delay_time}`}
            helperText='resources.queues.helperTexts.settings.busy_delay_time'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.call_timeout',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.call_timeout}
            source={`settings.${QueueSettingsFields.call_timeout}`}
            helperText='resources.queues.helperTexts.settings.call_timeout'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_caller_id_number',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_caller_id_number}
            source={`settings.${QueueSettingsFields.callback_caller_id_number}`}
            helperText='resources.queues.helperTexts.settings.callback_caller_id_number'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_caller_number_regex',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_caller_number_regex}
            source={`settings.${QueueSettingsFields.callback_caller_number_regex}`}
            helperText='resources.queues.helperTexts.settings.callback_caller_number_regex'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_delay',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_delay}
            source={`settings.${QueueSettingsFields.callback_delay}`}
            helperText='resources.queues.helperTexts.settings.callback_delay'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_retention',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_retention}
            source={`settings.${QueueSettingsFields.callback_retention}`}
            helperText='resources.queues.helperTexts.settings.callback_retention'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_enabled',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_enabled}
            source={`settings.${QueueSettingsFields.callback_enabled}`}
            helperText='resources.queues.helperTexts.settings.callback_enabled'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_retries',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_retries}
            source={`settings.${QueueSettingsFields.callback_retries}`}
            helperText='resources.queues.helperTexts.settings.callback_retries'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_timeout',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_timeout}
            source={`settings.${QueueSettingsFields.callback_timeout}`}
            helperText='resources.queues.helperTexts.settings.callback_timeout'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_username',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_username}
            source={`settings.${QueueSettingsFields.callback_username}`}
            helperText='resources.queues.helperTexts.settings.callback_username'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.callback_wait_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.callback_wait_time}
            source={`settings.${QueueSettingsFields.callback_wait_time}`}
            helperText='resources.queues.helperTexts.settings.callback_wait_time'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.continue_on_answer',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.continue_on_answer}
            source={`settings.${QueueSettingsFields.continue_on_answer}`}
            helperText='resources.queues.helperTexts.settings.continue_on_answer'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.continue_on_max_waiters',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.continue_on_max_waiters}
            source={`settings.${QueueSettingsFields.continue_on_max_waiters}`}
            helperText='resources.queues.helperTexts.settings.continue_on_max_waiters'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.daily_reset_stats',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.daily_reset_stats}
            source={`settings.${QueueSettingsFields.daily_reset_stats}`}
            helperText='resources.queues.helperTexts.settings.daily_reset_stats'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.daily_reset_stats_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <OrdDateTimeInput
            allDay={false}
            parse={dateParser}
            source={QueueUpdateFields['settings.daily_reset_stats_time']}
            helperText='resources.queues.helperTexts.settings.daily_reset_stats_time'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.exitkey',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.exitkey}
            source={`settings.${QueueSettingsFields.exitkey}`}
            helperText='resources.queues.helperTexts.settings.exitkey'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.ignore_unavail',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.ignore_unavail}
            source={`settings.${QueueSettingsFields.ignore_unavail}`}
            helperText='resources.queues.helperTexts.settings.ignore_unavail'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.max_exten',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.max_exten}
            source={`settings.${QueueSettingsFields.max_exten}`}
            helperText='resources.queues.helperTexts.settings.max_exten'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.max_wait_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.max_wait_time}
            source={`settings.${QueueSettingsFields.max_wait_time}`}
            helperText='resources.queues.helperTexts.settings.max_wait_time'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.no_answer_delay_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.no_answer_delay_time}
            source={`settings.${QueueSettingsFields.no_answer_delay_time}`}
            helperText='resources.queues.helperTexts.settings.no_answer_delay_time'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.reject_delay_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.reject_delay_time}
            source={`settings.${QueueSettingsFields.reject_delay_time}`}
            helperText='resources.queues.helperTexts.settings.reject_delay_time'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.service_level',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.service_level}
            source={`settings.${QueueSettingsFields.service_level}`}
            helperText='resources.queues.helperTexts.settings.service_level'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.skip_empty',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.skip_empty}
            source={`settings.${QueueSettingsFields.skip_empty}`}
            helperText='resources.queues.helperTexts.settings.skip_empty'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.weight',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.weight}
            source={`settings.${QueueSettingsFields.weight}`}
            helperText='resources.queues.helperTexts.settings.weight'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.queues.fields.settings.wrap_up_time',
      element: (
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput
            jsonSchema={jsonSchema}
            property={QueueSettingsFields.wrap_up_time}
            source={`settings.${QueueSettingsFields.wrap_up_time}`}
            helperText='resources.queues.helperTexts.settings.wrap_up_time'
          />
        </OrdFormList>
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};
